import { Check, X } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { Eyebrow, Reveal } from "./Reveal";

const ROWS = [
    {
        feature: "Accepted at dealership service counters",
        us: "Yes — nationwide",
        them: "Routinely refused",
    },
    {
        feature: "Sold through your local dealer",
        us: "Showroom & service lane",
        them: "TV ads, mailers & robocalls",
    },
    {
        feature: "Pays the dealer's retail labor rate",
        us: "Yes — in full",
        them: "Sub-rates — you cover the gap",
    },
    {
        feature: "Claims approved",
        us: "Minutes, at the counter",
        them: "Hours on hold with an adjuster",
    },
    {
        feature: "How you pay",
        us: "Subscription — pay as you go",
        them: "Large upfront contract price",
    },
    {
        feature: "Cancel anytime",
        us: "Yes — no penalty",
        them: "Locked-in multi-year terms",
    },
];

export const Comparison = () => {
    const content = useContent();

    return (
    <section
        id="compare"
        data-testid="comparison-section"
        className="relative overflow-hidden py-24 md:py-36"
    >
        <div
            className="absolute left-[-15%] top-1/3 h-[500px] w-[500px] rounded-full bg-sky-500/[0.07] blur-[140px]"
            aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
                <Eyebrow>{content.compare_eyebrow}</Eyebrow>
                <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
                    {content.compare_heading_a}{" "}
                    <span className="text-sky-400">
                        {content.compare_heading_b}
                    </span>
                </h2>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                    {content.compare_subtext}
                </p>
            </Reveal>

            <Reveal delay={0.1}>
                <div
                    data-testid="comparison-table"
                    className="mt-14 overflow-hidden rounded-3xl border border-white/10 bg-[#121722] md:mt-16"
                >
                    <div className="grid grid-cols-[1.2fr_1fr_1fr] border-b border-white/10 bg-[#182030]">
                        <div className="px-5 py-5 md:px-8" />
                        <div className="border-l border-white/10 px-4 py-5 md:px-8">
                            <p className="font-display text-sm font-bold text-sky-400 md:text-base">
                                AdvantaShield
                            </p>
                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Dealer-Native
                            </p>
                        </div>
                        <div className="border-l border-white/10 px-4 py-5 md:px-8">
                            <p className="font-display text-sm font-bold text-slate-400 md:text-base">
                                Legacy brands
                            </p>
                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                                Call-Center Era
                            </p>
                        </div>
                    </div>

                    {ROWS.map((row, i) => (
                        <div
                            key={row.feature}
                            data-testid={`comparison-row-${i}`}
                            className="grid grid-cols-[1.2fr_1fr_1fr] border-b border-white/5 transition-colors duration-300 last:border-b-0 hover:bg-white/[0.02]"
                        >
                            <p className="px-5 py-5 text-sm font-medium text-white md:px-8 md:text-base">
                                {row.feature}
                            </p>
                            <div className="flex items-start gap-2.5 border-l border-white/10 px-4 py-5 md:px-8">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-sky-400" />
                                <p className="text-xs leading-snug text-slate-300 md:text-sm">
                                    {row.us}
                                </p>
                            </div>
                            <div className="flex items-start gap-2.5 border-l border-white/10 px-4 py-5 md:px-8">
                                <X className="mt-0.5 h-4 w-4 shrink-0 text-red-400/80" />
                                <p className="text-xs leading-snug text-slate-500 md:text-sm">
                                    {row.them}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.15}>
                <p
                    data-testid="comparison-closing"
                    className="mt-10 max-w-2xl font-display text-xl font-semibold leading-snug text-white md:text-2xl"
                >
                    {content.compare_closing_a}{" "}
                    <span className="text-slate-500">
                        {content.compare_closing_b}
                    </span>
                </p>
            </Reveal>
        </div>
    </section>
    );
};
